/* screens/workspace/canvas/FoodcostCanvas.jsx — inline-канвас оптимизации food cost (S6).
   Read-only: метрики (current/target FC%, экономия), драйверы себестоимости, меры (было→стало
   строками + экономия + обоснование/компенсация), рекомендованные наборы, вердикт. Поля совпадают
   с боевым адаптером `adaptFoodcost` и рендерером ReportDetail.FoodcostReport (отчёт — генерируемый
   артефакт; правка result отложена — см. реестр дыр). ХЕНДОФФ → frontend/components/chat/FoodcostCanvas.jsx. */

import { Icon } from '../../../components/ui/Icon.jsx';
import { fmt } from '../../../lib/format.js';
import { RR } from '../../../lib/router.jsx';
import { FoodcostOptimizationReport } from '../../../components/reports/FoodcostOptimizationReport.jsx';
import { CanvasErrorState, CanvasMetrics, CanvasSection, CanvasSkeleton, CanvasViewForm, RiskTag, useCanvasLoad } from './CanvasViewForm.jsx';

  const { useNavigate } = RR;

  const TYPE_LABEL = { reduce_norm: 'Снизить норму', substitute: 'Замена', rebuild_semiproduct: 'Пересобрать п/ф', change_technology: 'Изменить технологию', change_purchase: 'Сменить закупку', change_price: 'Изменить цену' };

  function Drivers({ list }) {
    const max = Math.max(...list.map((d) => d.share_pct), 1);
    return (
      <div className="cv-drivers">
        {list.map((d, i) => (
          <div className="cv-driver" key={i}>
            <span className="cv-driver__name truncate">{d.ingredient_name}</span>
            <span className="cv-driver__bar"><span className="cv-driver__fill" style={{ width: (d.share_pct / max * 100) + '%' }} /></span>
            <span className="cv-driver__val num">{fmt.money(d.cost_rub)} · {fmt.percent(d.share_pct)}</span>
          </div>
        ))}
      </div>
    );
  }

  function MeasureCard({ m }) {
    return (
      <div className="cv-measure">
        <div className="cv-measure__head">
          <span className="cv-measure__type t-eyebrow">{TYPE_LABEL[m.type] || m.type}</span>
          <RiskTag risk={m.risk} />
        </div>
        {m.title && <p className="cv-measure__desc">{m.title}</p>}
        <div className="cv-measure__delta">
          {(m.before || m.after) && (
            <span className="cv-measure__pair">{m.before} <Icon name="arrow-right" size={12} /> <span className="cv-measure__to">{m.after}</span></span>
          )}
          <span className="cv-measure__pair">Экономия: <span className="num cv-measure__to">−{fmt.money(m.savings_rub)}</span>{m.savings_pct != null ? <span className="text-mute"> · {fmt.percent(m.savings_pct)}</span> : null}</span>
        </div>
        {m.justification && <p className="cv-measure__notes text-mute">{m.justification}</p>}
        {m.compensation && <p className="cv-measure__notes text-mute">Компенсация: {m.compensation}</p>}
      </div>
    );
  }

  function FoodcostCanvas({ spec, onClose, panel }) {
    const { kind, data, error, retry } = useCanvasLoad(spec);
    const navigate = useNavigate();

    const id = data ? data.id : spec.id;
    const openHref = `/reports/${id}`;
    const header = { eyebrow: 'Оптимизация food cost', icon: 'percent', title: (data && data.title) || 'Оптимизация food cost', panel };

    if (kind === 'loading') return <CanvasViewForm {...header} onClose={onClose}><CanvasSkeleton note="Считаем варианты…" /></CanvasViewForm>;
    if (kind === 'error') return <CanvasViewForm {...header} onClose={onClose}><CanvasErrorState error={error} message="Не удалось загрузить отчёт" notFoundMessage="Отчёт не найден или был удалён." onRetry={retry} /></CanvasViewForm>;
    if (kind === 'partial') return <CanvasViewForm {...header} onClose={onClose}><CanvasSkeleton note="Считаем варианты…" /></CanvasViewForm>;

    const r = data.result;
    // v2 (детерминированный пайплайн) — единый рендерер с кликабельными
    // тегами мер/риска (общий с детальной страницей); старые отчёты — прежний.
    if (r && r.v2) {
      const footerV2 = (
        <div className="cv-foot-actions">
          <button type="button" className="btn btn--primary btn--sm" onClick={() => navigate(openHref)}><span className="btn__label">Открыть отчёт</span><Icon name="arrow-up-right" size={14} /></button>
        </div>
      );
      return (
        <CanvasViewForm {...header} onClose={onClose} openHref={openHref} openLabel="Открыть" footer={footerV2}>
          <FoodcostOptimizationReport r={r} compact />
        </CanvasViewForm>
      );
    }
    const mt = r.metrics || {};
    const metrics = [
      { label: 'Текущий FC', value: mt.current_fc_pct != null ? fmt.percent(mt.current_fc_pct, { digits: 1 }) : '—', tone: 'bad' },
      { label: 'Целевой FC', value: mt.target_fc_pct != null ? fmt.percent(mt.target_fc_pct, { digits: 1 }) : '—', tone: 'ok' },
      { label: 'Экономия', value: fmt.money(mt.potential_savings_rub), tone: 'ok' },
    ];

    const body = (
      <>
        <CanvasMetrics items={metrics} />
        <CanvasSection title="Драйверы себестоимости"><Drivers list={r.cost_drivers || []} /></CanvasSection>
        <CanvasSection title="Меры оптимизации">
          <div className="cv-measures">
            {(r.measures || []).map((m, i) => <MeasureCard key={i} m={m} />)}
          </div>
        </CanvasSection>
        {r.recommendations && r.recommendations.length > 0 && (
          <CanvasSection title="Рекомендованные наборы">
            <div className="cv-recs">
              {r.recommendations.map((rec, i) => (
                <div className={'cv-rec' + (rec.label === 'Компромисс' ? ' cv-rec--pick' : '')} key={i}>
                  <span className="cv-rec__label">{rec.label}</span>
                  <span className="cv-rec__save num">−{fmt.money(rec.total_savings_rub)}</span>
                  <span className="cv-rec__meas t-caption text-mute">{(rec.measures_ids || []).join(' + ')}</span>
                </div>
              ))}
            </div>
          </CanvasSection>
        )}
        {r.verdict && (
          <div className="cv-callout"><Icon name="sparkles" size={16} /><p>{r.verdict}</p></div>
        )}
      </>
    );

    const footer = (
      <div className="cv-foot-actions">
        <button type="button" className="btn btn--primary btn--sm" onClick={() => navigate(openHref)}><span className="btn__label">Открыть отчёт</span><Icon name="arrow-up-right" size={14} /></button>
      </div>
    );
    return <CanvasViewForm {...header} onClose={onClose} openHref={openHref} openLabel="Открыть" footer={footer}>{body}</CanvasViewForm>;
  }

export { FoodcostCanvas };
